import { Form, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useContext, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import leiPrehledUctuApi from 'services/lei/PrehledUctu';
import {
    ILeiSpravaUctuResp,
    leiSpravaUctuApi,
    leiSpravaUctuPrevodApi,
    leiSpravaUctuZadostApi
} from 'services/lei/spravaUctu';
import nastavitUcetApi from 'services/nastavitUcet';

import { EStavKontrolaMigrace } from 'enums/lei/leiDataEnums';
import { ELeiGleifStav } from 'enums/lei/leiGleifStav';
import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import { validateLEI } from 'utils/validations/leiValidation';

interface ISpravaUctu {
    isNovyUcet?: boolean;
    setShowSpravaUctu?: () => void;
}

const SpravaUctu: React.FC<ISpravaUctu> = ({ isNovyUcet, setShowSpravaUctu }) => {
    const [form] = Form.useForm();
    const { navigateWorkaround } = useNavigateWorkaround();
    const { aktivniFirma, ucty, language } = useContext(Context);
    const { setLevel } = useProfily();
    const [spravaUctuResp, setSpravaUctuResp] = useState<ILeiSpravaUctuResp>();
    const [loading, setLoading] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'PRIDELENI' | 'ODESLANI'>();
    const [showPwField, setShowPwField] = useState<boolean>(false);
    const [neznamHeslo, setNeznamHeslo] = useState<boolean>(false);
    const [zadostAltText, setZadostAltText] = useState<boolean>(false);
    const [, setRerender] = useState<boolean>(false);
    const { FIRMA_PLAIN, LEI_DETAIL } = routePathEnum;
    const baseURL = `${FIRMA_PLAIN}${aktivniFirma?.iducet}`;

    return (
        <LoadingWrapper loading={loading}>
            <Title level={3} text="LEI_SPRAVOVANE" />
            <FormWrapper
                outForm={form}
                onFinish={vals => {
                    setNeznamHeslo(false);
                    const callApi = (iducet?: number) =>
                        leiSpravaUctuApi(vals.lei)
                            .then(resp => {
                                /*     if (
                    
                                    //alebo neplatne lei
                                ) {
                                    return Neplatný kód LEI
                                }*/
                                resp.stav === EStavKontrolaMigrace.STEJNYUCET && resp.varsymbol
                                    ? leiPrehledUctuApi().then(response => {
                                          setLevel('lei', {
                                              selectedLei: response.lei.filter(
                                                  e => e.varsymbol === String(resp?.varsymbol)
                                              )[0],
                                              idVarsymZadosti: {
                                                  varsym: String(resp.varsymbol)
                                              }
                                          });
                                          navigateWorkaround(
                                              `${FIRMA_PLAIN}${iducet || aktivniFirma?.iducet}` + LEI_DETAIL
                                          );
                                      })
                                    : resp.stav === EStavKontrolaMigrace.PREVODEMAIL
                                      ? setModalType('PRIDELENI')
                                      : setSpravaUctuResp(resp);
                            })
                            .finally(() => setLoading(false));

                    setLoading(true);
                    const spravceLeiUcet = ucty?.filter(e => e.typ === EProfilyTyp.SPRAVCELEI)[0];
                    isNovyUcet && spravceLeiUcet && aktivniFirma?.iducet !== spravceLeiUcet.iducet
                        ? (setLevel('aktivniFirma', spravceLeiUcet),
                          nastavitUcetApi(spravceLeiUcet.iducet)
                              .then(() => {
                                  callApi(spravceLeiUcet.iducet);
                              })
                              .catch(() => setLoading(false)))
                        : callApi();
                }}
            >
                <InputComponent
                    label="LEI_CELE"
                    name="lei"
                    type="text"
                    disabled={!!spravaUctuResp?.stav}
                    extramessage={
                        spravaUctuResp?.stavlei === ELeiGleifStav.ANNULLED ||
                        spravaUctuResp?.stavlei === ELeiGleifStav.DUPLICATE ||
                        spravaUctuResp?.stavlei === ELeiGleifStav.RETIRED
                            ? 'LEI_NEPLATNY_KOD'
                            : spravaUctuResp?.stav === EStavKontrolaMigrace.LEINEEXISTUJE
                              ? 'LEI_NEEVIDOVANE'
                              : spravaUctuResp?.stav === EStavKontrolaMigrace.DUPLICITA
                                ? 'LEI_ZADOST_DUPLICITA'
                                : ''
                    }
                    rules={[
                        () => ({
                            validator(_, value: string) {
                                if (!value || /^\d{10}$/.test(value) || validateLEI(value) === 0)
                                    return Promise.resolve();

                                return Promise.reject(<TranslateText>NEVALIDNI_LEI_VS</TranslateText>);
                            }
                        })
                    ]}
                    onChange={() => setRerender(pv => !pv)}
                />
                {
                    <>
                        <Paragraph
                            text={spravaUctuResp?.nazev || ''}
                            style={{
                                display: 'block'
                            }}
                        />
                        {spravaUctuResp?.ico && (
                            <Paragraph
                                text={'IČ' + ' ' + spravaUctuResp?.ico || ''}
                                style={{
                                    display: 'block',
                                    marginTop: 8,
                                    marginBottom: 8
                                }}
                            />
                        )}
                    </>
                }
                {(spravaUctuResp?.stavlei === ELeiGleifStav.TRANSFERRED ||
                    spravaUctuResp?.stav === EStavKontrolaMigrace.LEINEEXISTUJE) && (
                    <div
                        style={{
                            display: 'block',
                            position: 'relative',
                            top: -12
                        }}
                    >
                        <Paragraph text="LEI_NEEVIDOVANE_PARAGRAPH" />
                        <AnchorComponent
                            id="ucetCdcpRedirectBtn"
                            href="https://www.cdcp.cz/wp-content/uploads/CDCP_FORM_ZADOST_TRANSFER_LEI.pdf"
                            target="_blank"
                        >
                            <TranslateText>ZDE</TranslateText>.
                        </AnchorComponent>
                    </div>
                )}
                {showPwField && <InputComponent label="HESLO" name="heslo" type={'password'} required={true} />}
                {(spravaUctuResp?.stav === EStavKontrolaMigrace.ZADOST ||
                    spravaUctuResp?.stav === EStavKontrolaMigrace.PREVODHESLO) &&
                    !showPwField && (
                        <Paragraph
                            text={
                                neznamHeslo
                                    ? 'LEI_SPRAVA_UCTU_ZADOST_NEZNAM'
                                    : zadostAltText
                                      ? 'LEI_SPRAVA_UCTU_PW_NESPRAVNE'
                                      : `LEI_SPRAVA_UCTU_${spravaUctuResp?.stav}`
                            }
                            style={{
                                paddingBottom: 12,
                                display: 'block'
                            }}
                        />
                    )}

                <Row className="button-row">
                    {(spravaUctuResp?.stav || (isNovyUcet && !spravaUctuResp?.stav)) && !showPwField && (
                        <ButtonComponent
                            id="leiUcetZpetBtn"
                            buttontext="ZPET"
                            type="default"
                            htmlType="button"
                            onClick={() => {
                                if (!spravaUctuResp?.stav && isNovyUcet) {
                                    setShowSpravaUctu && setShowSpravaUctu();
                                } else {
                                    setSpravaUctuResp(undefined);
                                    setZadostAltText(false);
                                }
                            }}
                        />
                    )}
                    {!spravaUctuResp?.stav && (
                        <ButtonComponent
                            buttontext={'POKRACOVAT'}
                            id="leiUcetSubmitAltBtn"
                            type="primary"
                            onClick={() => form.submit()}
                            disabled={spravaUctuResp?.stav === EStavKontrolaMigrace.DUPLICITA}
                        />
                    )}
                    {spravaUctuResp?.stav === EStavKontrolaMigrace.ZADOST && (
                        <ButtonComponent
                            buttontext="ODESLAT_ZADOST"
                            id="leiUcetSubmitBtn"
                            type="primary"
                            disabled={
                                spravaUctuResp?.stavlei === ELeiGleifStav.ANNULLED ||
                                spravaUctuResp?.stavlei === ELeiGleifStav.DUPLICATE ||
                                spravaUctuResp?.stavlei === ELeiGleifStav.RETIRED
                            }
                            htmlType="button"
                            onClick={() => {
                                setLoading(true);
                                leiSpravaUctuZadostApi(form.getFieldValue('lei'), language)
                                    .then(() => setModalType('ODESLANI'))
                                    .finally(() => setLoading(false));
                            }}
                        />
                    )}
                    {spravaUctuResp?.stav === EStavKontrolaMigrace.PREVODHESLO && !showPwField && (
                        <>
                            <ButtonComponent
                                buttontext="ZNAM_HESLO"
                                id="leiUcetZnamPwBtn"
                                type="primary"
                                htmlType="button"
                                onClick={() => setShowPwField(true)}
                            />
                            <ButtonComponent
                                buttontext="NEZNAM_HESLO"
                                id="leiUcetNeznamPwBtn"
                                type="primary"
                                htmlType="button"
                                onClick={() => {
                                    setNeznamHeslo(true);
                                    setSpravaUctuResp(pv => {
                                        return {
                                            ...pv,
                                            stav: EStavKontrolaMigrace.ZADOST
                                        };
                                    });
                                }}
                            />
                        </>
                    )}
                    {showPwField && (
                        <>
                            <ButtonComponent
                                buttontext="ZPET"
                                type="default"
                                htmlType="button"
                                onClick={() => setShowPwField(false)}
                                id="leiUcetPwZpetBtn"
                            />
                            <ButtonComponent
                                buttontext="POKRACOVAT"
                                id="leiUcetPwPokracovatBtn"
                                type="primary"
                                htmlType="button"
                                onClick={() => form.validateFields().then(() => setModalType('PRIDELENI'))}
                            />
                        </>
                    )}
                </Row>

                <Modal
                    title={
                        <TranslateText>
                            {modalType === 'PRIDELENI' ? 'LEI_PRIDELENI_TITLE' : 'LEI_ZADOST_SPRAVA_TITLE'}
                        </TranslateText>
                    }
                    open={!!modalType}
                    onCancel={() => setModalType(undefined)}
                    afterClose={() => {
                        setModalType(undefined);
                    }}
                    footer={
                        <Row className="button-row-modal">
                            <ButtonComponent
                                id="leiUcetModalNeBtn"
                                buttontext="NE"
                                type="default"
                                htmlType="button"
                                onClick={() => {
                                    setModalType(undefined);
                                }}
                            />
                            <ButtonComponent
                                id="leiUcetModalAnoBtn"
                                buttontext="ANO"
                                htmlType="button"
                                onClick={() => {
                                    setLoading(true);
                                    leiSpravaUctuPrevodApi(form.getFieldsValue())
                                        .then(() => navigateWorkaround(baseURL + routePathEnum.LEI))
                                        .catch(
                                            err =>
                                                err.request.status === 401 &&
                                                (setSpravaUctuResp(pv => {
                                                    return {
                                                        ...pv,
                                                        stav: EStavKontrolaMigrace.ZADOST
                                                    };
                                                }),
                                                setShowPwField(false),
                                                setZadostAltText(true))
                                        )
                                        .finally(() => setLoading(false));
                                    setModalType(undefined);
                                }}
                            />
                        </Row>
                    }
                >
                    {modalType === 'PRIDELENI' ? (
                        <>
                            <Row>
                                <TranslateText params={[form.getFieldValue('lei')]}>LEI_PRIDELENI_TEXT</TranslateText>
                            </Row>
                        </>
                    ) : (
                        <>
                            <Row>
                                <Paragraph text="LEI_SPARAVA_ZADOST_SUCCESS_TITLE" strong={true} />
                                <Paragraph text="LEI_ZADOST_SPRAVA_TEXT" />
                            </Row>
                            <Row className="button-row">
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    htmlType="button"
                                    id="leiUcetPokracovatSuccessBtn"
                                    onClick={() => {
                                        navigateWorkaround(routePathEnum.MAIN_PAGE);
                                        setModalType(undefined);
                                    }}
                                />
                            </Row>
                        </>
                    )}
                </Modal>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default SpravaUctu;
